import { useState, useEffect } from "react";
import { FaFilter, FaTimes, FaSearch } from "react-icons/fa";

const InboxFilters = ({ filters, onApply, onClear, accounts = [], connectedAccountIds = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSince, setLocalSince] = useState(filters.since || "");
  const [localBefore, setLocalBefore] = useState(filters.before || "");
  const [localFrom, setLocalFrom] = useState(filters.from || "");
  const [localAccountIds, setLocalAccountIds] = useState(filters.mailAccountIds || []);
  const [accountSearchText, setAccountSearchText] = useState("");

  useEffect(() => {
    if (isOpen) {
      setLocalSince(filters.since || "");
      setLocalBefore(filters.before || "");
      setLocalFrom(filters.from || "");
      setLocalAccountIds(filters.mailAccountIds || []);
    }
  }, [isOpen, filters]);

  const hasActiveFilters = filters.since || filters.before || filters.from || (filters.mailAccountIds?.length > 0);

  const handleApply = () => {
    const next = {
      since: localSince.trim() || undefined,
      before: localBefore.trim() || undefined,
      from: localFrom.trim() || undefined,
      mailAccountIds: localAccountIds.length > 0 ? localAccountIds : undefined,
    };
    onApply(next);
    setIsOpen(false);
  };

  const handleClear = () => {
    setLocalSince("");
    setLocalBefore("");
    setLocalFrom("");
    setLocalAccountIds([]);
    onClear();
    setIsOpen(false);
  };

  const toggleAccount = (id) => {
    setLocalAccountIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const connectedAccounts = accounts.filter((a) => connectedAccountIds.includes(a.id));

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold rounded-md transition-colors
          ${hasActiveFilters ? "text-orange-600 bg-orange-50" : "text-gray-500 hover:bg-gray-100"}`}
      >
        <FaFilter className="text-[9px]" />
        Filtros
        {hasActiveFilters && (
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
        )}
      </button>

      {isOpen && (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Filtrar por</span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <FaTimes className="text-[10px]" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Desde</label>
              <input
                type="date"
                value={localSince}
                onChange={(e) => setLocalSince(e.target.value)}
                className="w-full px-2 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:border-orange-400"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Hasta</label>
              <input
                type="date"
                value={localBefore}
                onChange={(e) => setLocalBefore(e.target.value)}
                className="w-full px-2 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:border-orange-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Remitente</label>
            <input
              type="text"
              value={localFrom}
              onChange={(e) => setLocalFrom(e.target.value)}
              placeholder="email@ejemplo.com"
              className="w-full px-2 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:border-orange-400"
            />
          </div>

          {connectedAccounts.length > 1 && (
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">Cuentas</label>
              <div className="relative mb-2">
                <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
                <input
                  type="text"
                  value={accountSearchText}
                  onChange={(e) => setAccountSearchText(e.target.value)}
                  placeholder="Buscar cuenta..."
                  className="w-full pl-7 pr-2 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:border-orange-400"
                />
              </div>
              <div className="flex flex-wrap gap-1.5 items-center">
                <button
                  type="button"
                  onClick={() => setLocalAccountIds(connectedAccounts.map((a) => a.id))}
                  className="px-2 py-1 rounded text-[10px] font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 transition-colors"
                >
                  Todas
                </button>
                <button
                  type="button"
                  onClick={() => setLocalAccountIds([])}
                  className="px-2 py-1 rounded text-[10px] font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  Ninguna
                </button>
                {connectedAccounts
                  .filter((acc) => {
                    const q = accountSearchText.trim().toLowerCase();
                    if (!q) return true;
                    const nombre = (acc.nombre || "").toLowerCase();
                    const email = (acc.imapUser || "").toLowerCase();
                    return nombre.includes(q) || email.includes(q);
                  })
                  .map((acc) => (
                  <label
                    key={acc.id}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[10px] cursor-pointer hover:bg-gray-200"
                  >
                    <input
                      type="checkbox"
                      checked={localAccountIds.includes(acc.id)}
                      onChange={() => toggleAccount(acc.id)}
                      className="w-3 h-3 rounded-sm border-gray-400 hover:cursor-pointer"
                    />
                    <span className="truncate max-w-[140px]">{acc.imapUser}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 px-2 py-1.5 text-[11px] font-semibold bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
            >
              Aplicar
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="px-2 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
            >
              Limpiar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InboxFilters;
